import { getAndSanitize } from '../utils/sanitization';

interface UsePermissionsReturn {
  hasPermission: (endpoint: string, method: string) => boolean;
  getAllPermissions: () => Record<string, boolean>;
  hasSectionPermission: (section: string) => boolean;
}

export const usePermissions = (): UsePermissionsReturn => {
  // Obtener el rol directamente de localStorage para asegurar que esté actualizado
  const role = getAndSanitize('role');

  // Función para obtener todos los permisos
  const getAllPermissions = (): Record<string, boolean> => {
    // Si es admin, superadmin o soporte, tiene todos los permisos
    if (role === 'ADMIN' || role === 'SUPERADMIN' || role === 'SUPPORT') {
      return {};
    }
    
    // Para usuarios normales, obtener permisos del localStorage
    const permissionsJson = getAndSanitize('user_permissions');
    if (!permissionsJson) {
      console.warn('[Permissions] No permissions found in localStorage');
      return {};
    }
    
    try {
      return JSON.parse(permissionsJson);
    } catch (error) {
      console.error('[Permissions] Error parsing permissions from localStorage:', error);
      return {};
    }
  };
  
  // Función para verificar si el usuario tiene permiso para una operación específica
  const hasPermission = (endpoint: string, method: string): boolean => {
    // Si es admin, superadmin o soporte, tiene todos los permisos
    if (role === 'ADMIN' || role === 'SUPERADMIN' || role === 'SUPPORT') {
      return true;
    }
    
    // Para usuarios normales, verificar permiso en localStorage
    const permissions = getAllPermissions();
    const key = `${endpoint}:${method}`;
    
    // Si no hay un permiso específico, denegar por defecto
    if (!(key in permissions)) {
      console.warn(`[Permissions] No permission found for ${key}`);
      return false;
    }
    
    return permissions[key];
  };
  
  // Función para verificar permisos específicos de secciones
  const hasSectionPermission = (section: string): boolean => {
    // Mapeo de secciones a sus endpoints correspondientes
    const sectionEndpoints: Record<string, string> = {
      'operations': '/api/operations',
      'users': '/api/panel',
      'wallet': '/api/transfers',
      'settings': '/api/settings',
    };
    
    // Si la sección no está en el mapeo, permitir por defecto
    if (!sectionEndpoints[section.toLowerCase()]) {
      return true;
    }
    
    // Verificar el permiso GET para el endpoint correspondiente
    return hasPermission(sectionEndpoints[section.toLowerCase()], 'GET');
  };
  
  return {
    hasPermission,
    getAllPermissions,
    hasSectionPermission
  };
}; 